import { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';
import PropTypes from 'prop-types';

import {
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_CROSS_TABS,
  readSurveyResponsesSummaryGPT,
  SURVEY_TYPES,
} from '@api/instant_survey';

import { shareInstantSurveyWithClientGroup, shareInstantSurveyWithUsers } from '@api/surveys';

import {
  exportMaxDiffPrefShareData,
  exportRawData,
  exportRawDataV2,
  exportRawDataSpss,
  exportSummaryData,
  exportSummaryDataV2,
  exportStatXtabData,
  readGlobalFilters,
  readGlobalFiltersV2,
  readRawData,
  readRawDataV2,
  readSurveyDashboardRequests,
} from '@api/survey_dashboard';
import { GATES } from '@constants';

import { exportPowerPoint } from './utilities/helpers';

import {
  ButtonText,
  DownloadIcon,
  getFormattedDate,
  IconButton,
  Layout,
  LoaderSkeleton,
  MinusIcon,
  OverlayPopover,
  PlusIcon,
  SaveSmartPeopleGroupIcon,
  ShareIcon,
  Tabs,
  toast,
  Toolbar,
} from '@utilities';

import Details from './components/Details';
import InsightsDetails from './components/InsightsDetails';
import ModalShare from '../../../../components/ModalShare';
import ModalSurveyPeopleGroup from '../../../../components/ModalSurveyPeopleGroup';
import RawData from './components/RawData';
import Responses from './components/Responses';

import { SEARCH_PARAM_KEYS } from '../../utilities/constants';
import { useTheme } from '@utilities';
import { DISPLAY_AS } from './components/Responses/utilities/helpers';

import styles from './_index.module.scss';

const SURVEY_DASHBOARD_TABS = [
  { id: 'details', label: 'Survey Details' },
  { id: 'responses', label: 'Survey Responses' },
  { id: 'raw-data', label: 'Raw Data' },
];

const getDisplayAsFromParam = (displayAsParam) => {
  return displayAsParam === DISPLAY_AS.COUNT.value ? DISPLAY_AS.COUNT : DISPLAY_AS.PERCENT;
};

const Dashboard = ({
  details,
  insightsDocument,
  insightsType,
  publicToken,
  survey,
  surveyQuestions,
  surveyType,
}) => {
  const theme = useTheme();

  let { survey_id: surveyId, tab_id: tabId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const displayAs = getDisplayAsFromParam(searchParams.get(SEARCH_PARAM_KEYS.DISPLAY_AS));
  const [completionRateData, setCompletionRateData] = useState(null);
  const [keyFindingsMap, setKeyFindingsMap] = useState({});
  const [globalFilters, setGlobalFilters] = useState();
  const [globalFiltersError, setGlobalFiltersError] = useState();
  const [isFiltersLoading, setIsFiltersLoading] = useState(true);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isRawDataLoading, setIsRawDataLoading] = useState(true);
  const [isResponseDataLoading, setIsResponseDataLoading] = useState(true);
  const [rawData, setRawData] = useState();
  const [rawDataLoadingError, setRawDataLoadingError] = useState();
  const [responseData, setResponseData] = useState();
  const [responseLoadingError, setResponseLoadingError] = useState();
  const [sortByGroupByQuestion, setSortByGroupByQuestion] = useState({});
  const [sortOrderByQuestion, setSortOrderByQuestion] = useState({});
  const [showModalShare, setShowModalShare] = useState(false);
  const [showModalSurveyPeopleGroup, setShowModalSurveyPeopleGroup] = useState(false);
  const [showResearchObjective, setShowResearchObjective] = useState(false);
  const [includeDisqualifiedResponses, setIncludeDisqualified] = useState(false);
  const [responseCounts, setResponseCounts] = useState(null);

  const downloadButtonRef = useRef();
  const filtersRequestController = useRef();
  const isVideoSurvey = surveyType?.id === SURVEY_TYPES.VIDEO;
  const navigate = useNavigate();
  const rawDataRequestController = useRef();
  const responseRequestController = useRef();
  const sortByGroupByQuestionRef = useRef({});
  const sortOrderByQuestionRef = useRef({});

  const { client } = useStatsigClient();
  const canCrossTabs = useMemo(() => {
    return surveyQuestions?.some(({ type }) => QUESTION_TYPES_WITH_CROSS_TABS.includes(type));
  }, [surveyQuestions]);
  const canPreferenceShare = useMemo(() => {
    return (
      surveyQuestions?.some(
        ({ type }) =>
          type === QUESTION_TYPES.MAX_DIFF.externalType || type === QUESTION_TYPES.MAX_DIFF.value
      ) && client.checkGate(GATES.MAXDIFF_PREFERENCE_ANALYSIS)
    );
  }, [client, surveyQuestions]);
  const canIncludeDisqualifiedResponses = client.checkGate(
    GATES.SURVEY_INCLUDE_DISQUALIFIED_RESPONSES
  );
  const canShowResearchObjectives = client.checkGate(GATES.RESEARCH_OBJECTIVES);

  const compareByOptions = useMemo(
    () => [
      {
        label: 'Primary Segments',
        options: [
          { label: 'Overall', value: 'overall' },
          ...(globalFilters?.quotaGroups?.length > 1
            ? [{ label: 'Quota Group', value: 'quota_group' }]
            : []),
        ],
      },
      {
        label: 'Demographic Segments',
        options: globalFilters?.demographicFilters
          ?.filter(({ compareBy }) => compareBy)
          .map(({ id, label }) => ({
            label,
            value: id,
          })),
      },
    ],
    [globalFilters, survey]
  );
  const questionsRef = survey ? survey.questions : insightsDocument.questions;
  const selectedCompareBy = useMemo(() => {
    if (!globalFilters) return null;
    const compareByValue = searchParams.get(SEARCH_PARAM_KEYS.COMPARE_BY);
    if (!compareByValue)
      return globalFilters?.quotaGroups?.length > 1
        ? compareByOptions[0].options[1]
        : compareByOptions[0].options[0];

    return [...compareByOptions[0].options, ...compareByOptions[1].options].find(
      ({ value }) => value === compareByValue
    );
  }, [globalFilters, searchParams]);
  const selectedDemographicFilters = useMemo(() => {
    const searchParamsObject = Object.fromEntries(searchParams);

    return (
      Object.keys(searchParamsObject)
        // only include properties that are in the demographic filters
        .filter((key) => globalFilters?.demographicFilters.find(({ id }) => id === key))
        .reduce((acc, key) => {
          acc[key] = searchParamsObject[key]?.split(',');
          return acc;
        }, {})
    );
  }, [globalFilters, searchParams]);
  const selectedOptionIds = searchParams.get(SEARCH_PARAM_KEYS.QUESTION_OPTION)?.split(',') || [];
  const selectedQuotaGroups = useMemo(() => {
    return searchParams
      .get(SEARCH_PARAM_KEYS.QUOTA_GROUP)
      ?.split(',')
      .map((groupRef) =>
        globalFilters?.quotaGroups.find(
          ({ instantSurveyGroupRef }) => instantSurveyGroupRef === groupRef
        )
      )
      .filter(Boolean);
  }, [globalFilters, searchParams]);

  useEffect(() => {
    setSortByGroupByQuestion({});
    setSortOrderByQuestion({});
    sortByGroupByQuestionRef.current = {};
    sortOrderByQuestionRef.current = {};
  }, [selectedCompareBy?.value]);

  const exportQuery = useMemo(
    () => ({
      compare_by:
        selectedCompareBy?.value !== compareByOptions[0].options[0].value
          ? selectedCompareBy?.value
          : '',
      option_filter: selectedOptionIds.join(';'),
      quota_group_filter: selectedQuotaGroups
        ?.flatMap(({ quotaGroupRefs }) => quotaGroupRefs)
        .join(';'),
      ...Object.fromEntries(
        Object.entries(selectedDemographicFilters).map(([key, value]) => [key, value.join(';')])
      ),
    }),
    [
      selectedCompareBy,
      compareByOptions,
      selectedOptionIds,
      selectedQuotaGroups,
      selectedDemographicFilters,
    ]
  );

  useEffect(() => {
    // redirect to first tab if invalid tab id
    if (tabId && SURVEY_DASHBOARD_TABS.every(({ id }) => id !== tabId)) {
      const firstTabId = SURVEY_DASHBOARD_TABS[0].id;
      navigate(
        publicToken
          ? `/share/survey/${publicToken}/${insightsDocument.id}/${firstTabId}`
          : `/dashboard/survey/${insightsDocument.id}/${firstTabId}`,
        { replace: true }
      );
    }
  }, [tabId]);

  useEffect(() => {
    // Fetch GPT summaries and key findings when survey and compare-by selection change
    if (!survey?.id || !selectedCompareBy) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchGPTData = async () => {
      try {
        const gptData = await readSurveyResponsesSummaryGPT({
          signal,
          survey_id: survey.id,
        });

        // Set GPT data for key findings and response summaries
        setKeyFindingsMap(gptData);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn('[Dashboard] Failed to fetch GPT data:', error);
          setKeyFindingsMap({});
        }
      }
    };

    fetchGPTData();
    return () => controller.abort();
  }, [survey, selectedCompareBy]);

  useEffect(() => {
    getFilters();
    getRawData();

    return () => {
      filtersRequestController?.current?.abort();
      rawDataRequestController?.current?.abort();
    };
  }, [insightsDocument]);

  const searchParamsWithoutDisplayAs = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete(SEARCH_PARAM_KEYS.DISPLAY_AS);
    return params.toString();
  }, [searchParams]);

  useEffect(() => {
    if (globalFilters) {
      getResponseData();
    }

    return () => responseRequestController?.current?.abort();
  }, [globalFilters, includeDisqualifiedResponses, searchParamsWithoutDisplayAs]);

  const getFilters = async () => {
    const controller = new AbortController();
    filtersRequestController.current = controller;

    try {
      if (isVideoSurvey) {
        setGlobalFilters(
          await readGlobalFiltersV2({
            publicToken,
            signal: controller.signal,
            surveyId,
          })
        );
      } else {
        setGlobalFilters(
          await readGlobalFilters({
            publicToken,
            signal: controller.signal,
            surveyId,
          })
        );
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setGlobalFiltersError(error);
      }
    }

    setIsFiltersLoading(false);
  };

  const getRawData = async () => {
    const controller = new AbortController();
    rawDataRequestController.current = controller;
    setIsRawDataLoading(true);

    try {
      const params = { publicToken, signal: controller.signal, surveyId };
      const response = isVideoSurvey ? await readRawDataV2(params) : await readRawData(params);
      setRawData(response);
      setIsRawDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        setRawDataLoadingError(error);
      }
      setIsRawDataLoading(false);
    }
  };

  const getResponseData = async () => {
    responseRequestController?.current?.abort();
    const controller = new AbortController();
    responseRequestController.current = controller;

    setIsResponseDataLoading(true);
    setCompletionRateData(null);
    setResponseCounts(null);
    setResponseData(null);
    setResponseLoadingError(null);

    const params = {
      publicToken,
      query: { ...exportQuery, includeDisqualified: includeDisqualifiedResponses },
      signal: controller.signal,
      surveyId,
    };
    try {
      const { anyResponseFulfilled, completionRate, responseCounts, responseData } =
        await readSurveyDashboardRequests({
          includeCompletionRate: canIncludeDisqualifiedResponses,
          params,
          surveyQuestions,
        });

      if (controller.signal.aborted) return;

      if (responseCounts.status === 'fulfilled') {
        setResponseCounts(responseCounts.value);
      } else {
        console.warn(
          '[Dashboard] Failed to fetch readSurveyResponseCounts:',
          responseCounts.reason
        );
      }
      if (completionRate?.status === 'fulfilled') {
        setCompletionRateData(completionRate.value);
      } else if (completionRate?.status === 'rejected') {
        console.warn('[Dashboard] Failed to fetch readCompletionRate:', completionRate.reason);
      }
      if (!anyResponseFulfilled) {
        setResponseLoadingError(new Error('Failed to load survey response data.'));
        setIsResponseDataLoading(false);
        return;
      }
      setResponseData(
        responseData.reduce((acc, response) => {
          if (response?.id != null) acc[response.id] = response;
          return acc;
        }, {})
      );

      setIsResponseDataLoading(false);
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        setResponseLoadingError(error);
        setIsResponseDataLoading(false);
      }
    }
  };
  const handleExportPowerPoint = async () => {
    const surveyResponsesArray = responseData ? Object.values(responseData).filter(Boolean) : [];
    if (!surveyResponsesArray.length) {
      toast.error(
        'No survey response data available for PowerPoint export. Please ensure the survey responses have loaded.'
      );
      return;
    }

    toast('Generating PowerPoint, please wait...');
    try {
      await exportPowerPoint({
        details,
        displayAs,
        globalFilters,
        insightsDocument,
        keyFindingsMap,
        questionsRef,
        responseData,
        searchParams,
        selectedCompareBy,
        sortByGroupByQuestion: sortByGroupByQuestionRef.current,
        sortOrderByQuestion: sortOrderByQuestionRef.current,
        survey,
        surveyQuestions,
        theme,
      });
      toast.success('PowerPoint file has been downloaded');
    } catch (error) {
      console.error('PowerPoint generation error:', error);
      toast.error('Failed to generate PowerPoint presentation: ' + error.message);
    }
  };

  const handleExportDownload = async (exportDownloadAPI) => {
    try {
      await exportDownloadAPI({ query: exportQuery, surveyId });
    } catch (error) {
      console.error('Error occurred while exporting data', error);
      toast.error('Error occurred while exporting data.');
    }
  };

  const handleUpdateIncludeDisqualified = () => setIncludeDisqualified((prev) => !prev);

  const areExportOpsDisabled = isRawDataLoading || isResponseDataLoading;
  const downloadTooltipText = areExportOpsDisabled
    ? 'Download will be available after report has finished processing'
    : 'Download data';
  const surveyPeopleGroupTooltipText = areExportOpsDisabled
    ? 'Save Survey People Group will be available after report has finished processing'
    : 'Save Survey People Group';

  const canCreateSurveyPeopleGroups = client.checkGate(GATES.CREATE_SURVEY_PEOPLE_GROUPS);

  const surveyTitle = survey?.title || insightsDocument.title;

  const handleQuestionSortByChange = (questionId, value) => {
    const prev = sortByGroupByQuestionRef.current;
    let nextState = prev;

    if (!value || value === 'default') {
      if (questionId in prev) {
        nextState = { ...prev };
        delete nextState[questionId];
      }
    } else if (prev[questionId] !== value) {
      nextState = {
        ...prev,
        [questionId]: value,
      };
    }

    if (nextState !== prev) {
      sortByGroupByQuestionRef.current = nextState;
      setSortByGroupByQuestion(nextState);
    }
  };

  const handleQuestionSortOrderChange = (questionId, value) => {
    const prev = sortOrderByQuestionRef.current;
    let nextState = prev;

    if (!value || value === 'default') {
      if (questionId in prev) {
        nextState = { ...prev };
        delete nextState[questionId];
      }
    } else if (prev[questionId] !== value) {
      nextState = {
        ...prev,
        [questionId]: value,
      };
    }

    if (nextState !== prev) {
      sortOrderByQuestionRef.current = nextState;
      setSortOrderByQuestion(nextState);
    }
  };

  const handleDisplayAsChange = (newDisplayAs) => {
    searchParams.set(SEARCH_PARAM_KEYS.DISPLAY_AS, newDisplayAs.value);
    setSearchParams(searchParams);
  };

  return (
    <>
      <div>
        <Toolbar
          tools={
            publicToken
              ? []
              : [
                  <IconButton
                    isDisabled={areExportOpsDisabled}
                    key="download-button"
                    onClick={() => setIsPopoverOpen(true)}
                    ref={downloadButtonRef}
                    tooltip={downloadTooltipText}
                  >
                    <DownloadIcon />
                  </IconButton>,
                  <>
                    {!isVideoSurvey && canCreateSurveyPeopleGroups ? (
                      <IconButton
                        isDisabled={areExportOpsDisabled}
                        key="save-survey-people-group"
                        onClick={() => setShowModalSurveyPeopleGroup(true)}
                        tooltip={surveyPeopleGroupTooltipText}
                      >
                        <SaveSmartPeopleGroupIcon />
                      </IconButton>
                    ) : null}
                  </>,
                  <IconButton key="share" onClick={() => setShowModalShare(true)} tooltip={'Share'}>
                    <ShareIcon />
                  </IconButton>,
                  <OverlayPopover
                    isActive={isPopoverOpen}
                    key="download-popover"
                    onHide={() => setIsPopoverOpen(false)}
                    target={downloadButtonRef?.current}
                  >
                    <ul>
                      <li>
                        <button
                          onClick={() =>
                            handleExportDownload(
                              isVideoSurvey ? exportSummaryDataV2 : exportSummaryData
                            )
                          }
                        >
                          <span>Summary Tables</span>
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() =>
                            handleExportDownload(isVideoSurvey ? exportRawDataV2 : exportRawData)
                          }
                        >
                          <span>Raw Data</span>
                        </button>
                      </li>
                      {!isVideoSurvey && (
                        <li>
                          <button onClick={() => handleExportDownload(exportRawDataSpss)}>
                            <span>SPSS</span>
                          </button>
                        </li>
                      )}
                      {canCrossTabs && (
                        <li>
                          <button onClick={() => handleExportDownload(exportStatXtabData)}>
                            <span>Crosstabs & Stat Testing</span>
                          </button>
                        </li>
                      )}
                      {canPreferenceShare && (
                        <li>
                          <button onClick={() => handleExportDownload(exportMaxDiffPrefShareData)}>
                            <span>Preference Share</span>
                          </button>
                        </li>
                      )}
                      {!isVideoSurvey && (
                        <li>
                          <button onClick={() => handleExportPowerPoint()}>
                            <span>PowerPoint</span>
                          </button>
                        </li>
                      )}
                    </ul>
                  </OverlayPopover>,
                ]
          }
        >
          <h2>{surveyTitle}</h2>
        </Toolbar>
        {canShowResearchObjectives && survey?.researchObjectives?.trim() && (
          <div className={styles['dashboard-research-objectives-collapsible']}>
            {showResearchObjective ? (
              <>
                <p className={styles['dashboard-research-objectives']}>
                  {survey.researchObjectives}
                </p>
                <ButtonText onClick={() => setShowResearchObjective(false)}>
                  <MinusIcon />
                  <span>
                    Hide <strong>Objective</strong>
                  </span>
                </ButtonText>
              </>
            ) : (
              <ButtonText onClick={() => setShowResearchObjective(true)}>
                <PlusIcon />
                <span>
                  Show <strong>Objective</strong>
                </span>
              </ButtonText>
            )}
          </div>
        )}
        <div>
          <p>
            {survey && survey.issueDateTime ? (
              <>
                Survey fielded on{' '}
                <strong>{getFormattedDate(new Date(survey.issueDateTime))}</strong>
              </>
            ) : (
              <>
                Survey created on{' '}
                <strong>{getFormattedDate(new Date(insightsDocument.created_datetime))}</strong>
              </>
            )}
          </p>
          {survey?.groups?.length > 1 && (
            <p>
              <strong>{survey.groups.length}</strong> quota groups
            </p>
          )}
          <p>
            <strong>{details?.completes || insightsDocument.completed}</strong>{' '}
            {survey?.groups?.length > 1 ? 'completed responses' : 'responses'}
          </p>
          <p>
            <strong>{questionsRef.length}</strong>{' '}
            {questionsRef.length === 1 ? 'question' : 'questions'}
          </p>
        </div>
        {showModalShare && (
          <ModalShare
            hasAllColleagues
            hasFindColleagues
            hasSharePublicly
            ids={[Number(surveyId)]}
            isSurvey
            setShowModalShare={setShowModalShare}
            shareItemWithClients={shareInstantSurveyWithClientGroup}
            shareItemWithUsers={shareInstantSurveyWithUsers}
          />
        )}

        {showModalSurveyPeopleGroup && (
          <ModalSurveyPeopleGroup
            compareBy={
              selectedCompareBy?.value !== compareByOptions[0].options[0].value
                ? selectedCompareBy?.value
                : ''
            }
            demographicFilter={selectedDemographicFilters}
            globalFilters={globalFilters}
            optionFilter={selectedOptionIds}
            quotaGroupFilter={selectedQuotaGroups}
            sampleSize={responseCounts?.filteredResponses || 0}
            setShowModalSurveyPeopleGroup={setShowModalSurveyPeopleGroup}
            surveyId={Number(surveyId)}
            surveyQuestions={surveyQuestions}
            surveyTitle={surveyTitle}
            totalResponses={responseCounts?.totalResponses || 0}
          />
        )}
        <hr />
      </div>

      <Tabs
        routes={SURVEY_DASHBOARD_TABS.map(({ id, label }) => ({
          is_active: id === tabId,
          label,
          to: publicToken
            ? `/share/survey/${publicToken}/${surveyId}/${id}${window.location.search}`
            : `/dashboard/survey/${surveyId}/${id}${window.location.search}`,
        }))}
      />
      {tabId === 'details' && (
        <Layout.Column>
          {survey ? (
            <Details
              details={details}
              survey={survey}
              surveyQuestions={surveyQuestions}
              surveyType={surveyType}
            />
          ) : (
            <InsightsDetails
              details={details}
              survey={insightsDocument}
              surveyQuestions={surveyQuestions}
              surveyType={insightsType}
            />
          )}
        </Layout.Column>
      )}
      {tabId === 'responses' && (
        <Responses
          compareByOptions={compareByOptions}
          completionRateData={completionRateData}
          displayAs={displayAs}
          exportQuery={exportQuery}
          globalFilters={globalFilters}
          globalFiltersError={globalFiltersError}
          handleUpdateIncludeDisqualified={handleUpdateIncludeDisqualified}
          includeDisqualifiedResponses={includeDisqualifiedResponses}
          isDataLoading={isResponseDataLoading}
          isFiltersLoading={isFiltersLoading}
          isVideoSurvey={isVideoSurvey}
          onDisplayAsChange={handleDisplayAsChange}
          onQuestionSortByChange={handleQuestionSortByChange}
          onQuestionSortOrderChange={handleQuestionSortOrderChange}
          responseData={responseData}
          responseError={responseLoadingError}
          selectedCompareBy={selectedCompareBy}
          sortByGroupByQuestion={sortByGroupByQuestion}
          sortOrderByQuestion={sortOrderByQuestion}
          survey={survey}
          surveyQuestions={surveyQuestions}
          surveyResponseSummariesMap={keyFindingsMap}
        />
      )}
      {tabId === 'raw-data' && (
        <RawData
          isRawDataLoading={isRawDataLoading}
          isTooMuchData={
            (details?.completes || insightsDocument.completed) > details?.rawDataRowLimit
          }
          rawData={rawData}
          rawDataLoadingError={rawDataLoadingError}
        />
      )}
    </>
  );
};

Dashboard.propTypes = {
  details: PropTypes.object,
  id: PropTypes.number,
  insightsDocument: PropTypes.object.isRequired,
  insightsType: PropTypes.object,
  publicToken: PropTypes.string,
  survey: PropTypes.object,
  surveyQuestions: PropTypes.arrayOf(PropTypes.object),
  surveyType: PropTypes.object,
};

export const DashboardSkeleton = () => (
  <LoaderSkeleton className={styles['dashboard-skeleton']} height={725}>
    <rect height="95" rx="4" ry="4" width="850" x="0" y="0" />
    <rect height="40" rx="4" ry="4" width="100%" x="0" y="105" />
    <rect height="30" rx="4" ry="4" width="300" x="0" y="155" />
    <rect height="165" rx="4" ry="4" width="100%" x="0" y="195" />
    <rect height="30" rx="4" ry="4" width="300" x="0" y="380" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="420" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="488" />
    <rect height="58" rx="4" ry="4" width="100%" x="0" y="556" />
  </LoaderSkeleton>
);

const DashboardWithTabs = (props) => {
  return (
    <Routes>
      <Route element={<Dashboard {...props} />} path=":tab_id" />
      {/* cover case when no tab is selected */}
      <Route element={<Navigate replace to={SURVEY_DASHBOARD_TABS[0].id} />} path="*" />
    </Routes>
  );
};

export default Object.assign(DashboardWithTabs, {
  Skeleton: DashboardSkeleton,
});
